import { Injectable, computed, inject, signal } from '@angular/core';

import {
  type ActivityDetailEditableField,
  INITIAL_ACTIVITY_DETAIL_FIELD_SAVE_STATUSES,
  createInitialActivityDetailFieldSaveGenerations,
} from '@features/kt/activity-browser/constants/activity-inline-edit.constants';
import { ActivityService } from '@features/kt/activity-browser/services/activity.service';

import {
  COVER_GENERATION_QUEUE,
  createdCoverCandidate,
  loadedCoverCandidate,
} from '@shared/models/cover-generation-queue.port';

import { httpErrorStatus, isTransientHttpError } from '@shared/utils/http-error-status.util';
import { httpErrorTitle } from '@shared/utils/http-error-title.util';

import {
  type ActivityContextDocument,
  ActivityDocumentLinkType,
  type ActivityFieldLayoutItem,
  type ActivityInstanceDetail,
  type ActivityPedagogicalCustomFieldResponse,
  type ActivityPedagogicalFieldResponse,
  type CreateActivityPedagogicalCustomFieldRequest,
  type PedagogicalFieldName,
  type UpdateActivityPedagogicalCustomFieldRequest,
} from '@api';

import { StatusCodes } from 'http-status-codes';

import {
  mergeCustomPedagogicalFieldListFromServer,
  mergeCustomPedagogicalFieldRowFromServer,
  orderedCustomPedagogicalFields,
} from '../constants/activity-edit-pedagogical-custom.utils';
import {
  mergePedagogicalFieldListFromServer,
  orderedPedagogicalFieldNames,
  pedagogicalVisibilityToggleFieldName,
  withPedagogicalFieldAtPosition,
  withPedagogicalSortOrderFromBaseline,
  withPedagogicalVisibilityOptimistic,
} from '../constants/activity-edit-pedagogical.utils';
import {
  type ActivityEditDeepLinkOutcome,
  type ActivityEditDraft,
  type ActivityEditFieldCommit,
  type ActivityEditFieldSaveStatus,
  type ActivityEditFieldSaveStatuses,
  type ActivityEditFlushResult,
  mapActivityInstanceDetailToEditDraft,
} from '../models/activity-edit-field.model';
import {
  type CustomPedagogicalPersistBindings,
  cloneCustomPedagogicalFieldRows,
  optimisticCustomPedagogicalDelete,
  optimisticCustomPedagogicalMetadata,
  optimisticCustomPedagogicalReorder,
  optimisticCustomPedagogicalVisibility,
  persistCreateCustomPedagogicalField,
  persistCustomPedagogicalReorder,
  persistCustomPedagogicalVisibility,
  persistDeleteCustomPedagogicalField,
  persistUpdateCustomPedagogicalField,
} from './activity-edit-custom-pedagogical.store-ops';

/**
 * How one tracked dispatch ended. `notCounted` marks the saves a flush awaits but never reports (context link/unlink
 * and the field-layout save keep their own rollback); `superseded` is a field save a newer one replaced.
 */
type TrackedSaveOutcome = 'failed' | 'imageSourceRejected' | 'notCounted' | 'saved' | 'superseded';

/**
 * Kontextus (context-document) ceiling for an owned activity instance (KT-200.4, contract C10,
 * scenario S1). Mirrors the backend `ActivityInstance.MaxContextDocuments = 20`; at this count the
 * add is inactive and {@link ActivityEditStore.remainingContextSlots} is 0.
 */
export const MAX_CONTEXT_DOCUMENTS = 20;

/** ProblemDetails `title` for a refused section image source (contract C2, scenario S14). */
export const ACTIVITY_CONTENT_SECTION_IMAGE_SOURCE_NOT_ALLOWED = 'ActivityContentSection.ImageSourceNotAllowed';

/** ProblemDetails `title` when the extra-field set no longer matches the reorder request (KT-660). */
export const PEDAGOGICAL_FIELD_REORDER_STALE = 'PedagogicalField.ReorderStale';

export { CUSTOM_PEDAGOGICAL_FIELD_REORDER_STALE } from '../constants/activity-edit-pedagogical-custom.constants';

export type ActivityEditSilentReloadResult = 'done' | 'failed' | 'retry';

/**
 * Page-level editor store for the dedicated activity edit page.
 *
 * Mirrors the plain-signal store pattern of `guided-activity-create.store.ts` and is
 * provided via the edit route's `providers` (NOT `providedIn: 'root'`), so each edit
 * session gets a fresh instance. It loads the teacher's OWN activity instance into an
 * editable draft and orchestrates per-field autosave — porting the KT-155 modal
 * save-orchestration (per-field generation counter guarding against late/out-of-order
 * responses) to a page-level store (contracts C1/C8, scenario S28).
 *
 * FE-1 scope: store + models + commit plumbing. The `name` commit path is wired
 * end-to-end through `ActivityService.updateName`; the remaining field bindings
 * (description, steps, chips) land in later tasks.
 */
// Route-scoped by design (contract C6 / decision Q3): provided in the edit route's
// `providers`, never application-wide, so each edit session gets a fresh draft.
// eslint-disable-next-line @angular-eslint/use-injectable-provided-in -- route-scoped store, provided via route `providers`
@Injectable()
export class ActivityEditStore {
  readonly #activityService = inject(ActivityService);
  readonly #coverQueue = inject(COVER_GENERATION_QUEUE);

  readonly #draft = signal<ActivityEditDraft | null>(null);
  readonly #fieldSaveStatuses = signal<ActivityEditFieldSaveStatuses>({
    ...INITIAL_ACTIVITY_DETAIL_FIELD_SAVE_STATUSES,
  });
  readonly #isLoading = signal(false);
  readonly #loadError = signal<unknown>(null);
  /**
   * Raised when a field autosave fails with an HTTP 404 — or a silent {@link reloadSilently} finds the
   * activity gone (404) or no longer owned — the row became unresolvable mid-edit
   * (deleted, or ownership lost). Distinct from a generic save error (5xx / network / uncaught 400,
   * which only reverts the field and reports `error`, S18a): reverting alone would mislead the
   * teacher into "editing" a row that no longer exists, so this flag lets the page disable editing
   * and redirect to the library with a distinct message — the same end-state as an unresolvable
   * deep-link (S18b / S25). Cleared per edit session in {@link #resetSessionState}.
   */
  readonly #saveUnavailable = signal(false);
  /**
   * Monotonic counter bumped on each successful field autosave or Kontextus mutation (KT-200.5,
   * contract C2). The edit page listens for increments to fire the one-shot multi-project snackbar.
   * Not incremented on validation skip (no dispatch), failed HTTP, or stale-generation discards.
   */
  readonly #successfulPersistCount = signal(0);
  /**
   * Bumped when a sections autosave is rejected for a foreign image source (KT-681 / S14). The edit
   * page listens for increments and opens a snackbar; the teacher's optimistic sections stay in the draft.
   */
  readonly #sectionImageSourceRejected = signal(0);
  readonly #sectionImageSourceRejectedHeld = signal(false);
  readonly #failedPedagogicalValueKeys = signal<ReadonlySet<string>>(new Set());
  /**
   * Monotonic count of drafts that landed from the server (a successful {@link load} or an own-instance
   * {@link resolveDeepLink}). Unlike {@link draft}, it does not change on an autosave reconcile and survives
   * {@link reset}, so a consumer can tell "a load landed" from "the draft was edited". A silent
   * {@link reloadSilently} does NOT bump it, so the sticker store is never reseeded from a reload.
   */
  readonly #draftLoadCount = signal(0);
  readonly #pedagogicalVisibilitySaveError = signal<unknown>(null);
  readonly #pedagogicalValueSaveStatus = signal<ActivityEditFieldSaveStatus>('idle');

  readonly isLoaded = computed(() => this.#draft() !== null);
  readonly name = computed(() => this.#draft()?.name ?? '');
  readonly description = computed(() => this.#draft()?.description ?? '');
  readonly fieldLayout = computed(() => this.#draft()?.fieldLayout ?? []);
  readonly pedagogicalFields = computed(() => this.#draft()?.pedagogicalFields ?? []);
  readonly customPedagogicalFields = computed(() => this.#draft()?.customPedagogicalFields ?? []);
  readonly isOwner = computed(() => this.#draft()?.isOwner ?? false);
  readonly duration = computed(() => this.#draft()?.duration);
  readonly tags = computed(() => this.#draft()?.tags ?? []);
  readonly instructions = computed(() => this.#draft()?.instructions ?? []);
  readonly sections = computed(() => this.#draft()?.sections ?? []);
  readonly subTopicId = computed(() => this.#draft()?.subTopicId);
  readonly isFavorite = computed(() => this.#draft()?.isFavorite ?? false);
  readonly isAiGenerated = computed(() => this.#draft()?.isAiGenerated ?? false);
  readonly visibility = computed(() => this.#draft()?.visibility);
  /**
   * The draft's persistently linked Kontextus documents (KT-200.4, contract C10). A DISTINCT field
   * from {@link ActivityEditStore.name}/attachments — the teacher's own `Indexed` source files
   * (`{ id, fileName }`), mutated via {@link linkContextDocuments} / {@link unlinkContextDocument},
   * NOT the `commitField` path.
   */
  readonly contextDocuments = computed<ActivityContextDocument[]>(() => this.#draft()?.contextDocuments ?? []);
  /**
   * How many more Kontextus documents may be linked: {@link MAX_CONTEXT_DOCUMENTS} − current count,
   * clamped to 0 (scenario S1). At the 20-document ceiling this is 0 and the add is inactive.
   */
  readonly remainingContextSlots = computed(() => Math.max(0, MAX_CONTEXT_DOCUMENTS - this.contextDocuments().length));
  /** True while the draft holds content the server rejected but the store kept, which a reload must not overwrite. */
  readonly hasUnsavedDraftContent = computed(
    () => this.#sectionImageSourceRejectedHeld() || this.#failedPedagogicalValueKeys().size > 0,
  );

  readonly #pedagogicalValueGeneration = new Map<string, number>();
  #pedagogicalValueSavesInFlight = 0;
  /**
   * Per-field generation counter. The commit that owns the highest generation for a
   * field wins; a resolving response whose generation is stale is discarded (S28).
   */
  #saveGeneration: Record<ActivityDetailEditableField, number> = createInitialActivityDetailFieldSaveGenerations();
  /**
   * Serialization tail for the shared `PUT …/tags` endpoint (Q21, contract C1). All six
   * chip dimensions (type/subtype/workform/grade/subject/tools) commit through the SAME
   * `tags` endpoint, which replaces the FULL tag set. Committing two dimensions within one
   * debounce window could cross-write each other (a request built on an earlier read would
   * drop the sibling change on re-sync). To bound that race, at most ONE `tags` request is
   * ever in flight: each tag commit's dispatch is chained onto the previous one, so the
   * next request starts only after the previous response has been processed and the tag
   * state updated. The per-field generation counter (S28) still guards late responses on
   * top of this. Reset per edit session in {@link load} / {@link reset}.
   */
  #tagsSaveQueue: Promise<unknown> = Promise.resolve();
  /**
   * Serialization tail for the immediate Kontextus link/unlink POST/DELETE (KT-200.4, contract C10,
   * scenario S1). Mirrors {@link #tagsSaveQueue}: at most ONE context request is ever in flight, so
   * two concurrent link/unlink calls cannot interleave and each reconciles against the server's
   * source-of-truth list in the order it was issued. The per-operation generation counter
   * ({@link #contextSaveGeneration}) still guards a stale/out-of-order response on top of this.
   *
   * The queue ALSO threads the last server-settled context list forward: each op resolves to the
   * freshest server-confirmed list it observed (its reconciled list on success — including on a stale
   * success, where the server persisted the change even though a newer op owns the draft — or the
   * unchanged baseline it was handed on failure), and the next op reads THAT as its rollback baseline.
   * This is why a failure rolls back to the settled state rather than a per-op draft snapshot — the
   * draft snapshot would still carry a prior queued op's never-persisted optimistic rows, leaving a
   * phantom row when two ops fail in a row. Resolves to `undefined` only while idle (before the FIRST
   * op after a load / reset), signalling that op to seed the baseline from the current draft; once any
   * op has run the tail always carries a concrete list. Reset per edit session in
   * {@link #resetSessionState}.
   */
  #contextSaveQueue: Promise<ActivityContextDocument[] | undefined> = Promise.resolve(undefined);
  /** Serializes `PUT …/field-layout` so rapid visibility toggles do not interleave (KT-903). */
  #fieldLayoutSaveQueue: Promise<void> = Promise.resolve();
  /** Serializes pedagogical visibility PUTs and extra-field reorders (KT-660). */
  #pedagogicalVisibilitySaveQueue: Promise<void> = Promise.resolve();
  /** Serializes custom pedagogical field configuration writes (KT-1081). */
  #customFieldConfigSaveQueue: Promise<void> = Promise.resolve();
  /** Last server-confirmed pedagogical list; rollback baseline for visibility saves. */
  #settledPedagogicalFields: ActivityPedagogicalFieldResponse[] | null = null;
  /** Last server-confirmed custom pedagogical list; rollback baseline for visibility saves. */
  #settledCustomPedagogicalFields: ActivityPedagogicalCustomFieldResponse[] | null = null;
  readonly pedagogicalVisibilitySaveError = this.#pedagogicalVisibilitySaveError.asReadonly();
  readonly pedagogicalValueSaveStatus = this.#pedagogicalValueSaveStatus.asReadonly();
  /**
   * Monotonic Kontextus mutation generation. Each link/unlink dispatch claims the next generation at
   * call time; a resolving response whose generation is no longer current is discarded, so a late
   * response can never overwrite newer context state (mirrors the per-field {@link #saveGeneration}
   * late-save guard). Reset per edit session in {@link #resetSessionState}.
   */
  #contextSaveGeneration = 0;
  /**
   * Monotonic deep-link resolution epoch. Each {@link resolveDeepLink} call takes a fresh epoch
   * up front; after EVERY await boundary the resolution re-checks it is still the current epoch
   * before writing any shared signal or issuing a side effect (`copyActivity`). A fast `:id`
   * change (browser back/forward, the fork URL swap) starts a newer resolution with a higher
   * epoch, so the older one becomes stale and bails out with `superseded`.
   *
   * What the epoch check GUARANTEES for a stale resolution: it writes no draft / `isLoading` /
   * `loadError`, it performs no navigation, and — when it is superseded BEFORE the fork is
   * dispatched (i.e. during the instance- or template-GET) — it issues NO `:copy`. In that window
   * a stale resolution has no observable side effect at all.
   *
   * What it does NOT undo (accepted best-effort limit): the epoch can only be re-checked AFTER an
   * await settles, never mid-flight. So if the supersession lands WHILE `copyActivity` is already
   * in flight, the copy has ALREADY been created on the server and cannot be recalled from the
   * client. The post-await check then returns `superseded` (so the page does NOT swap the URL to
   * it), but an "orphan" private copy may linger in the teacher's library. A POST-based fork is not
   * client-recallable, so this is accepted, consistent with the plan's Q6 client-side-dedup
   * tech-debt; a compensating server-side delete is a separate product decision (out of scope).
   *
   * Not part of the per-edit-session reset — it must survive {@link #resetSessionState} so an
   * in-flight stale resolution is still detectable after the newer one has reset the session state.
   */
  #resolveEpoch = 0;
  /**
   * In-flight autosave dispatches. Every `commitField` dispatch (value fields directly, chip
   * dimensions via the serialized `tags` tail) is registered here and self-removes when it
   * settles. {@link flushPending} awaits this set so a route-leave guard can wait for a valid
   * change whose save is still pending in the debounce window to finish before navigation.
   * `#dispatchAndReconcile` handles its own errors and never rejects, so a failed save does
   * not stall the flush; it resolves to the {@link TrackedSaveOutcome} the flush reports.
   */
  readonly #pendingSaves = new Set<Promise<TrackedSaveOutcome>>();
  #saveDispatchCount = 0;
  /**
   * One list per running {@link flushPending}. A dispatch records its outcome into every list the moment it
   * settles, so a flush also sees a save that started and settled while it was still awaiting an older one.
   */
  readonly #flushOutcomes = new Set<TrackedSaveOutcome[]>();
  readonly draft = this.#draft.asReadonly();
  readonly fieldSaveStatuses = this.#fieldSaveStatuses.asReadonly();
  readonly isLoading = this.#isLoading.asReadonly();
  readonly loadError = this.#loadError.asReadonly();
  /**
   * True once a field autosave has failed with a 404, or a silent {@link reloadSilently} found the
   * activity gone (404) or no longer owned (the activity became unresolvable mid-edit).
   * The page reacts by disabling editing and redirecting to the library with a distinct message
   * (S18b / S25). Reset on the next {@link load} / {@link resolveDeepLink} / {@link reset}.
   */
  readonly saveUnavailable = this.#saveUnavailable.asReadonly();
  readonly successfulPersistCount = this.#successfulPersistCount.asReadonly();
  readonly sectionImageSourceRejected = this.#sectionImageSourceRejected.asReadonly();
  readonly draftLoadCount = this.#draftLoadCount.asReadonly();

  /**
   * Loads the teacher's own instance into the editable draft.
   *
   * @param id The activity instance id to load.
   */
  async load(id: string): Promise<void> {
    // Reset per-edit-session state so a re-entrant load never inherits stale
    // generation counters or field save statuses from a prior draft.
    this.#resetSessionState();
    this.#isLoading.set(true);
    this.#loadError.set(null);

    try {
      const detail = await this.#activityService.getActivityInstanceById(id);
      this.#draft.set(mapActivityInstanceDetailToEditDraft(detail));
      this.#draftLoadCount.update((count) => count + 1);
      if (detail.isOwner) {
        await this.#loadPedagogicalAndCustomFieldsIntoDraft(id, null);
      }
      this.#coverQueue.remember(detail.id, detail);
      if (detail.isOwner) {
        this.#coverQueue.enqueue(loadedCoverCandidate(detail));
      }
    } catch (error) {
      this.#loadError.set(error);
    } finally {
      this.#isLoading.set(false);
    }
  }

  /**
   * Resolves a deep-link `activity/:id/edit` id (FE-10, contracts C5/C6, scenarios S5/S6/S7/S25).
   *
   * Instance-first resolution (reusing the `openDetailFromDeepLink()` order): the caller's OWN
   * instance loads directly and NEVER forks (S7) — this is also what a refresh of an
   * already-forked URL resolves to, so a reload cannot create a second copy (S6). A 404 on the
   * instance means it is not ours; if it then resolves as a library template, a private copy is
   * forked via the `{resource}:copy` seam (S5, C5) and the page swaps the URL to it. If neither
   * resolves — deleted, never-existed, or another teacher's private instance, all 404 (S25) — NO
   * `:copy` is ever issued and the page redirects to the library with an error snackbar.
   *
   * Routing itself stays a Presentation concern: this method decides the outcome, the page
   * performs the follow-up navigation (so the store never imports the route table).
   *
   * Race safety (epoch guard): a fast `:id` change (browser back/forward, or the fork URL swap
   * itself) can start a newer resolution while this one is still awaiting a GET / `:copy`. Each
   * call takes a monotonic {@link #resolveEpoch} up front and re-checks it after EVERY await
   * boundary — before writing any shared signal (`#draft` / `#isLoading` / `#loadError`) and
   * before issuing `copyActivity`. If a newer resolution has since started, this one is stale and
   * returns `superseded`: it writes no draft / loading / error and performs no navigation, so it
   * can never overwrite the fresh URL's draft. The newest resolution always wins.
   *
   * The `:copy` side effect is guaranteed skipped ONLY when the supersession happens BEFORE the
   * fork is dispatched (during the instance- or template-GET) — the epoch re-check there bails out
   * before `copyActivity` is ever called. If instead the supersession lands WHILE `copyActivity`
   * is already in flight, the copy has ALREADY been created server-side and is not recalled from
   * the client: the method still returns `superseded` (the page does not swap the URL to it), but
   * an orphan private copy may remain. This is an accepted best-effort limit — see
   * {@link #resolveEpoch} and {@link #forkTemplate}, and the plan's Q6 client-side-dedup tech-debt.
   *
   * @param id The activity id from the edit route.
   * @returns The resolution outcome for the page to act on.
   */
  async resolveDeepLink(id: string): Promise<ActivityEditDeepLinkOutcome> {
    this.#resetSessionState();
    // Claim this resolution's epoch BEFORE any await, so a later `:id` change that starts a newer
    // resolution bumps the epoch and marks everything below as stale (S5/S6/S7 race safety). Each
    // phase re-checks the epoch after its await and bails out with `superseded` if overtaken.
    const epoch = ++this.#resolveEpoch;
    this.#isLoading.set(true);
    this.#loadError.set(null);

    // 1) Instance-first (S7/S6): own instance → `loaded`; non-404 → `error`; a resolved outcome
    // here short-circuits. `null` means a 404 ("not mine") → fall through to the template probe.
    const instanceOutcome = await this.#resolveOwnInstance(id, epoch);
    if (instanceOutcome) {
      return instanceOutcome;
    }

    // 2) Not our instance → is it a library template? A resolved outcome here (`unresolvable` /
    // `error` / `superseded`) short-circuits; `null` means the template resolved → fork it.
    const templateOutcome = await this.#probeTemplate(id, epoch);
    if (templateOutcome) {
      return templateOutcome;
    }

    // 3) Library item → fork-on-edit (S5, C5).
    return this.#forkTemplate(id, epoch);
  }

  /**
   * Commits a single field: mark `saving`, apply the optimistic update, call the
   * per-field `ActivityService` update, then merge ONLY the committed field's slice
   * from the response onto the current draft and mark `saved`. A response is discarded
   * if a newer generation exists for that field (S28, C1); on error the field's slice
   * reverts to its pre-commit value and the status becomes `error`.
   *
   * @param commit The field commit (identifier + typed value) to persist.
   * @returns A promise that settles when this commit's dispatch and reconciliation finish.
   */
  async commitField(commit: ActivityEditFieldCommit): Promise<void> {
    const previous = this.#draft();
    if (!previous) {
      return;
    }

    const { field } = commit;
    const generation = this.#nextGeneration(field);
    // The optimistic update + `saving` status apply IMMEDIATELY for every field, so the UI
    // reflects the selection even while a tag dispatch waits behind the serialization queue.
    this.#setFieldSaveStatus(field, 'saving');
    this.#draft.set(this.#applyOptimisticUpdate(previous, commit));

    if (this.#isTagsField(field)) {
      // Serialize commits on the shared `tags` endpoint (Q21): chain this dispatch onto the
      // previous tags request so only one `PUT …/tags` is ever in flight, and the next one
      // starts only after the previous response is processed. `#dispatchAndReconcile` never
      // rejects (it handles its own errors), so the queue tail cannot stall on a failed save.
      const run = this.#tagsSaveQueue.then(() => this.#dispatchAndReconcile(previous, commit, generation));
      this.#tagsSaveQueue = run;
      return this.#trackPending(run);
    }

    return this.#trackPending(this.#dispatchAndReconcile(previous, commit, generation));
  }

  /**
   * Awaits every in-flight autosave dispatch so a route-leave guard can wait for a still-pending
   * valid change to be persisted before navigation proceeds. Resolves immediately when
   * nothing is pending. Loops until the set drains, in case a queued `tags` dispatch starts a
   * follow-up while an earlier one settles. Never rejects — a failed save resolves (its slice is
   * reverted internally), so navigation is never blocked.
   *
   * The result covers the per-field `commitField` saves that settle while the flush runs, whether they were
   * already in flight or started meanwhile: the sticky `fieldSaveStatuses` of an earlier save are
   * not consulted, a superseded save is not a failure (its newer save counts), and context link/unlink and
   * field-layout saves are awaited but never counted.
   *
   * @returns `failed` when such a field save failed, `failedAlreadyNotified` when the only failure is a
   *   rejected section image source (the page already showed its own snackbar), otherwise `ok`.
   */
  async flushPending(): Promise<ActivityEditFlushResult> {
    const outcomes: TrackedSaveOutcome[] = [];
    this.#flushOutcomes.add(outcomes);
    try {
      while (this.#pendingSaves.size > 0) {
        // `allSettled` snapshots the iterable synchronously, so passing the live Set is safe even
        // though tracked entries self-remove as they settle.
        await Promise.allSettled(this.#pendingSaves);
      }
    } finally {
      this.#flushOutcomes.delete(outcomes);
    }

    if (outcomes.includes('failed')) {
      return 'failed';
    }
    return outcomes.includes('imageSourceRejected') ? 'failedAlreadyNotified' : 'ok';
  }

  /**
   * Replaces the draft with the server state without a loading state. Never rejects. On the write path the
   * reloaded cover reaches the cover queue, like a landed {@link load}'s, so an MCP- or peer-set cover shows.
   *
   * @param canApply Checked right before the write.
   * @returns `'retry'` when nothing was written because a save interfered, unsaved content is held or
   * `canApply` returned false; `'failed'` when a network error or 5xx kept the server state out of reach.
   */
  async reloadSilently(canApply: () => boolean): Promise<ActivityEditSilentReloadResult> {
    const draft = this.#draft();
    if (!draft || this.#isLoading() || this.#saveUnavailable() || this.#loadError() !== null) {
      return 'done';
    }
    if (this.#pendingSaves.size > 0 || this.hasUnsavedDraftContent()) {
      return 'retry';
    }

    const { id } = draft;
    const epoch = this.#resolveEpoch;
    const dispatches = this.#saveDispatchCount;

    const [detailResult, pedagogicalResult, customResult] = await Promise.allSettled([
      this.#activityService.getActivityInstanceById(id),
      this.#activityService.getPedagogicalFields(id),
      this.#activityService.getPedagogicalCustomFields(id),
    ]);

    if (this.#resolveEpoch !== epoch || this.#draft()?.id !== id) {
      return 'done';
    }
    if (this.#saveDispatchCount !== dispatches) {
      return 'retry';
    }

    if (detailResult.status === 'rejected') {
      if (this.#isNotFound(detailResult.reason)) {
        this.#saveUnavailable.set(true);
        return 'done';
      }
      return isTransientHttpError(detailResult.reason) ? 'failed' : 'done';
    }

    const detail = detailResult.value;
    if (!detail.isOwner) {
      this.#saveUnavailable.set(true);
      return 'done';
    }

    if (
      [pedagogicalResult, customResult].some(
        (result) => result.status === 'rejected' && isTransientHttpError(result.reason),
      )
    ) {
      return 'failed';
    }

    if (!canApply()) {
      return 'retry';
    }

    const reloadedPedagogicalFields = pedagogicalResult.status === 'fulfilled' ? pedagogicalResult.value.items : null;
    const reloadedCustomFields = customResult.status === 'fulfilled' ? customResult.value.items : null;
    this.#replaceDraftFromReload(detail, reloadedPedagogicalFields, reloadedCustomFields);
    this.#coverQueue.remember(detail.id, detail);
    return 'done';
  }

  /**
   * Links a batch of Kontextus documents to the loaded instance (KT-200.4, contract C10, S1).
   *
   * Takes the FULL `{ id, fileName }` refs (from the dialog / upload result) so the optimistic rows
   * render real file names, not id-only placeholders. The optimistic rows apply IMMEDIATELY; the
   * POST is serialized on {@link #contextSaveQueue} (only one context request in flight) and, on
   * success, the draft's context list is reconciled from the returned detail (server is the source
   * of truth). On a 400 / network error the list rolls back to the server-settled baseline. A stale
   * response is discarded by the generation guard. Immediate, NOT the `commitField` path.
   *
   * @param refs The full `{ id, fileName }` refs to link.
   * @returns A promise that settles when this link's dispatch and reconciliation finish.
   */
  async linkContextDocuments(refs: ActivityContextDocument[]): Promise<void> {
    // Never dispatch an empty batch: the link endpoint requires 1–20 ids (BE-5 `NotEmpty`), so an
    // empty POST is a guaranteed 400. A no-op here keeps every caller safe, not just the edit page.
    if (refs.length === 0) {
      return;
    }

    return this.#enqueueContextMutation(
      (draft) => this.#withOptimisticLinkedRows(draft, refs),
      (id) =>
        this.#activityService.linkContextDocuments(
          id,
          refs.map((ref) => ref.id),
        ),
    );
  }

  /**
   * Unlinks a single Kontextus document from the loaded instance (KT-200.4, contract C10, S1).
   *
   * The row is removed optimistically at once; the DELETE is serialized on
   * {@link #contextSaveQueue} and, on success, the draft's context list is reconciled from the
   * returned detail. On a 400 / network error the list rolls back to the server-settled baseline; a
   * stale response is discarded by the generation guard. Immediate, NOT the `commitField` path.
   *
   * @param documentId The id of the document to unlink.
   * @returns A promise that settles when this unlink's dispatch and reconciliation finish.
   */
  async unlinkContextDocument(documentId: string): Promise<void> {
    return this.#enqueueContextMutation(
      (draft) => draft.contextDocuments.filter((doc) => doc.id !== documentId),
      (id) => this.#activityService.unlinkContextDocument(id, documentId),
    );
  }

  /**
   * Persists editor field visibility / order via `PUT …/field-layout` (KT-903, full replace).
   * Applies the optimistic layout immediately, serializes PUTs, reconciles from the response, and
   * reloads layout from the server on failure.
   *
   * @param layout The full layout payload from the gear menu.
   * @returns The result of this operation.
   */
  updateFieldLayout(layout: readonly ActivityFieldLayoutItem[]): Promise<void> {
    const previous = this.#draft();
    if (!previous) {
      return Promise.resolve();
    }

    const optimistic = layout.map((item) => ({ ...item }));
    this.#draft.update((cur) => (cur ? { ...cur, fieldLayout: optimistic } : cur));

    const run = this.#fieldLayoutSaveQueue.then(() => this.#persistFieldLayout());
    this.#fieldLayoutSaveQueue = run;
    return this.#trackPending(this.#asNotCounted(run));
  }

  /**
   * Persists pedagogical field visibility via `PUT …/pedagogical-fields/visibility` (body: fieldName, visible)
   * (KT-660). Optimistic update, serialized PUTs, merge from full list response, rollback on error.
   *
   * @param fieldName - Toggled pedagogical field name (content-goal pair normalized).
   * @param visible - New visibility for the field (and its pair partner when applicable).
   * @returns Promise that settles when the serialized save attempt finishes.
   */
  setPedagogicalVisibility(fieldName: string, visible: boolean): Promise<void> {
    const draft = this.#draft();
    if (!draft) {
      return Promise.resolve();
    }

    const toggleName = pedagogicalVisibilityToggleFieldName(fieldName);
    const beforeOptimistic = draft.pedagogicalFields.map((item) => ({ ...item }));
    const optimistic = withPedagogicalVisibilityOptimistic(draft.pedagogicalFields, toggleName, visible);
    this.#draft.update((cur) => (cur ? { ...cur, pedagogicalFields: optimistic } : cur));
    this.#pedagogicalVisibilitySaveError.set(null);

    const run = this.#pedagogicalVisibilitySaveQueue.then(() => {
      // Read the baseline when this save starts, after any reorder already on the queue has settled.
      const baseline = (this.#settledPedagogicalFields ?? beforeOptimistic).map((item) => ({ ...item }));
      return this.#persistPedagogicalVisibility(draft.id, toggleName, visible, baseline);
    });
    this.#pedagogicalVisibilitySaveQueue = run;
    return this.#trackPending(this.#asNotCounted(run));
  }

  /**
   * Persists pedagogical field order via `POST …/pedagogical-fields:reorder` (KT-660).
   * Optimistic full-list reorder, same queue as visibility. A stale set reloads the list and keeps
   * dirty values. Any other failure restores `sortOrder` from the settled list.
   *
   * @param fieldName - Pedagogical field that moved.
   * @param position - Target slot in the full list (1-based), as sent to `POST …/pedagogical-fields:reorder`.
   * @returns Promise that settles when the serialized save attempt finishes.
   */
  reorderPedagogicalField(fieldName: string, position: number): Promise<void> {
    const draft = this.#draft();
    if (!draft) {
      return Promise.resolve();
    }

    const currentPosition = orderedPedagogicalFieldNames(draft.pedagogicalFields).indexOf(fieldName) + 1;
    if (currentPosition === position || currentPosition === 0) {
      return Promise.resolve();
    }

    const beforeOptimistic = draft.pedagogicalFields.map((item) => ({ ...item }));
    const optimistic = withPedagogicalFieldAtPosition(draft.pedagogicalFields, fieldName, position);
    this.#draft.update((cur) => (cur ? { ...cur, pedagogicalFields: optimistic } : cur));
    this.#pedagogicalVisibilitySaveError.set(null);

    const run = this.#pedagogicalVisibilitySaveQueue.then(() => {
      const baseline = (this.#settledPedagogicalFields ?? beforeOptimistic).map((item) => ({ ...item }));
      return this.#persistPedagogicalReorder(draft.id, fieldName, position, baseline);
    });
    this.#pedagogicalVisibilitySaveQueue = run;
    return this.#trackPending(this.#asNotCounted(run));
  }

  /**
   * Creates an activity-owned custom pedagogical field (KT-1081).
   *
   * @param request - Create payload from the gear menu form.
   * @returns Promise that settles when the serialized create attempt finishes.
   */
  createCustomPedagogicalField(request: CreateActivityPedagogicalCustomFieldRequest): Promise<void> {
    const draft = this.#draft();
    if (!draft?.isOwner) {
      return Promise.resolve();
    }

    const run = this.#customFieldConfigSaveQueue.then(() =>
      this.#persistCreateCustomPedagogicalField(draft.id, request),
    );
    this.#customFieldConfigSaveQueue = run;
    return this.#trackPending(this.#asNotCounted(run));
  }

  /**
   * Updates custom field metadata (name, description, enum options) via `PUT …/pedagogical-custom-fields/{fieldId}`.
   *
   * @param fieldId - Custom field row id.
   * @param body - Metadata patch from the gear menu edit form.
   * @returns Promise that settles when the serialized update attempt finishes.
   */
  updateCustomPedagogicalField(fieldId: string, body: UpdateActivityPedagogicalCustomFieldRequest): Promise<void> {
    const draft = this.#draft();
    if (!draft?.isOwner) {
      return Promise.resolve();
    }

    const beforeOptimistic = cloneCustomPedagogicalFieldRows(draft.customPedagogicalFields);
    const optimistic = optimisticCustomPedagogicalMetadata(draft.customPedagogicalFields, fieldId, body);
    this.#draft.update((cur) => (cur ? { ...cur, customPedagogicalFields: optimistic } : cur));

    const run = this.#customFieldConfigSaveQueue.then(() => {
      const baseline = cloneCustomPedagogicalFieldRows(this.#settledCustomPedagogicalFields ?? beforeOptimistic);
      return this.#persistUpdateCustomPedagogicalField(draft.id, fieldId, body, baseline);
    });
    this.#customFieldConfigSaveQueue = run;
    return this.#trackPending(this.#asNotCounted(run));
  }

  /**
   * Persists custom pedagogical field visibility via `PUT …/pedagogical-custom-fields/{fieldId}/visibility`.
   *
   * @param fieldId - Custom field row id.
   * @param visible - New visibility for the field.
   * @returns Promise that settles when the serialized save attempt finishes.
   */
  setCustomPedagogicalVisibility(fieldId: string, visible: boolean): Promise<void> {
    const draft = this.#draft();
    if (!draft?.isOwner) {
      return Promise.resolve();
    }

    const beforeOptimistic = cloneCustomPedagogicalFieldRows(draft.customPedagogicalFields);
    const optimistic = optimisticCustomPedagogicalVisibility(draft.customPedagogicalFields, fieldId, visible);
    this.#draft.update((cur) => (cur ? { ...cur, customPedagogicalFields: optimistic } : cur));

    const run = this.#customFieldConfigSaveQueue.then(() => {
      const baseline = cloneCustomPedagogicalFieldRows(this.#settledCustomPedagogicalFields ?? beforeOptimistic);
      return this.#persistCustomPedagogicalVisibility(draft.id, fieldId, visible, baseline);
    });
    this.#customFieldConfigSaveQueue = run;
    return this.#trackPending(this.#asNotCounted(run));
  }

  /**
   * Persists custom pedagogical field order via `POST …/pedagogical-custom-fields:reorder`.
   *
   * @param fieldId - Custom field row id.
   * @param position - Target position within the custom-only list (1-based).
   * @returns Promise that settles when the serialized save attempt finishes.
   */
  reorderCustomPedagogicalField(fieldId: string, position: number): Promise<void> {
    const draft = this.#draft();
    if (!draft?.isOwner) {
      return Promise.resolve();
    }

    const ordered = orderedCustomPedagogicalFields(draft.customPedagogicalFields);
    const currentPosition = ordered.findIndex((item) => item.id === fieldId) + 1;
    if (currentPosition === position || currentPosition === 0) {
      return Promise.resolve();
    }

    const beforeOptimistic = cloneCustomPedagogicalFieldRows(draft.customPedagogicalFields);
    const optimistic = optimisticCustomPedagogicalReorder(draft.customPedagogicalFields, fieldId, position);
    this.#draft.update((cur) => (cur ? { ...cur, customPedagogicalFields: optimistic } : cur));

    const run = this.#customFieldConfigSaveQueue.then(() => {
      const baseline = cloneCustomPedagogicalFieldRows(this.#settledCustomPedagogicalFields ?? beforeOptimistic);
      return this.#persistCustomPedagogicalReorder(draft.id, fieldId, position, baseline);
    });
    this.#customFieldConfigSaveQueue = run;
    return this.#trackPending(this.#asNotCounted(run));
  }

  /**
   * Permanently deletes a custom pedagogical field via `DELETE …/pedagogical-custom-fields/{fieldId}`.
   *
   * @param fieldId - Custom field row id.
   * @returns Promise that settles when the serialized delete attempt finishes.
   */
  deleteCustomPedagogicalField(fieldId: string): Promise<void> {
    const draft = this.#draft();
    if (!draft?.isOwner) {
      return Promise.resolve();
    }

    const beforeOptimistic = cloneCustomPedagogicalFieldRows(draft.customPedagogicalFields);
    const optimistic = optimisticCustomPedagogicalDelete(draft.customPedagogicalFields, fieldId);
    this.#draft.update((cur) => (cur ? { ...cur, customPedagogicalFields: optimistic } : cur));

    const run = this.#customFieldConfigSaveQueue.then(() => {
      const baseline = cloneCustomPedagogicalFieldRows(this.#settledCustomPedagogicalFields ?? beforeOptimistic);
      return this.#persistDeleteCustomPedagogicalField(draft.id, fieldId, baseline);
    });
    this.#customFieldConfigSaveQueue = run;
    return this.#trackPending(this.#asNotCounted(run));
  }

  /**
   * Persists one pedagogical field value via `PUT …/pedagogical-fields/{fieldName}/value`.
   * Keeps the typed value when the save fails. A later response for the same field does not
   * overwrite a newer edit.
   *
   * @param fieldName - Pedagogical field whose value changed.
   * @param value - New stored value, or `null` when cleared.
   * @returns Promise that settles when the value save attempt finishes.
   */
  commitPedagogicalValue(fieldName: string, value: string | null): Promise<void> {
    const draft = this.#draft();
    if (!draft) {
      return Promise.resolve();
    }

    const current = draft.pedagogicalFields.find((item) => item.fieldName === fieldName);
    if (!current || current.value === value) {
      return Promise.resolve();
    }

    const generation = (this.#pedagogicalValueGeneration.get(fieldName) ?? 0) + 1;
    this.#pedagogicalValueGeneration.set(fieldName, generation);
    this.#pedagogicalValueSavesInFlight += 1;
    this.#pedagogicalValueSaveStatus.set('saving');
    this.#draft.update((cur) =>
      cur
        ? {
            ...cur,
            pedagogicalFields: cur.pedagogicalFields.map((item) =>
              item.fieldName === fieldName ? { ...item, value } : item,
            ),
          }
        : cur,
    );

    const run = this.#persistPedagogicalValue(draft.id, fieldName, value, generation);
    return this.#trackPending(run);
  }

  /**
   * Persists one custom pedagogical field value via `PUT …/pedagogical-custom-fields/{fieldId}/value`.
   *
   * @param fieldId - Custom field row id.
   * @param value - New stored value, or `null` when cleared.
   * @returns Promise that settles when the value save attempt finishes.
   */
  commitCustomPedagogicalValue(fieldId: string, value: string | null): Promise<void> {
    const draft = this.#draft();
    if (!draft) {
      return Promise.resolve();
    }

    const current = draft.customPedagogicalFields.find((item) => item.id === fieldId);
    if (!current || current.value === value) {
      return Promise.resolve();
    }

    const generation = (this.#pedagogicalValueGeneration.get(fieldId) ?? 0) + 1;
    this.#pedagogicalValueGeneration.set(fieldId, generation);
    this.#pedagogicalValueSavesInFlight += 1;
    this.#pedagogicalValueSaveStatus.set('saving');
    this.#draft.update((cur) =>
      cur
        ? {
            ...cur,
            customPedagogicalFields: cur.customPedagogicalFields.map((item) =>
              item.id === fieldId ? { ...item, value } : item,
            ),
          }
        : cur,
    );

    const run = this.#persistCustomPedagogicalValue(draft.id, fieldId, value, generation);
    return this.#trackPending(run);
  }

  /** Resets the store to its initial (empty) state. */
  reset(): void {
    this.#draft.set(null);
    this.#isLoading.set(false);
    this.#loadError.set(null);
    this.#resetSessionState();
  }

  /**
   * Registers an in-flight dispatch in {@link #pendingSaves} so {@link flushPending} can await it,
   * and self-removes it once it settles. The dispatch resolves even on save failure
   * (`#dispatchAndReconcile` handles its own errors), so the entry is always cleaned up.
   *
   * @param run The dispatch promise to track; it resolves to the outcome a flush reports.
   * @returns A promise that settles with the dispatch and drops its outcome, so callers can still await this
   *   commit directly.
   */
  #trackPending(run: Promise<TrackedSaveOutcome>): Promise<void> {
    this.#saveDispatchCount += 1;
    this.#pendingSaves.add(run);
    const settle = (outcome: TrackedSaveOutcome): void => {
      this.#pendingSaves.delete(run);
      for (const outcomes of this.#flushOutcomes) {
        outcomes.push(outcome);
      }
    };
    run.then(settle, () => {
      settle('failed');
    });
    return run.then(() => undefined);
  }

  #markPedagogicalValueFailed(key: string, failed: boolean): void {
    this.#failedPedagogicalValueKeys.update((keys) => {
      if (keys.has(key) === failed) {
        return keys;
      }

      const next = new Set(keys);
      if (failed) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }

  #replaceDraftFromReload(
    detail: ActivityInstanceDetail,
    reloadedPedagogicalFields: readonly ActivityPedagogicalFieldResponse[] | null,
    reloadedCustomFields: readonly ActivityPedagogicalCustomFieldResponse[] | null,
  ): void {
    const pedagogicalFields = (reloadedPedagogicalFields ?? this.pedagogicalFields()).map((item) => ({ ...item }));
    const customPedagogicalFields = (
      reloadedCustomFields ? orderedCustomPedagogicalFields(reloadedCustomFields) : this.customPedagogicalFields()
    ).map((item) => ({ ...item }));
    this.#draft.set({
      ...mapActivityInstanceDetailToEditDraft(detail),
      pedagogicalFields,
      customPedagogicalFields,
    });
    this.#settledPedagogicalFields = pedagogicalFields.map((item) => ({ ...item }));
    this.#settledCustomPedagogicalFields = customPedagogicalFields.map((item) => ({ ...item }));
    this.#contextSaveQueue = Promise.resolve(undefined);
  }

  /**
   * Marks a dispatch a flush awaits but never reports. A rejection is swallowed here too, so it can never be
   * counted as a field failure.
   *
   * @param run The context or field-layout dispatch.
   * @returns A promise that always resolves to `notCounted`.
   */
  #asNotCounted(run: Promise<unknown>): Promise<TrackedSaveOutcome> {
    return run.then(
      (): TrackedSaveOutcome => 'notCounted',
      (): TrackedSaveOutcome => 'notCounted',
    );
  }

  /**
   * Resets the per-edit-session save state (field statuses, generation counters, tags
   * serialization tail) so a re-entrant load / deep-link resolution never inherits stale
   * counters or statuses from a prior draft.
   */
  #resetSessionState(): void {
    this.#fieldSaveStatuses.set({ ...INITIAL_ACTIVITY_DETAIL_FIELD_SAVE_STATUSES });
    this.#saveGeneration = createInitialActivityDetailFieldSaveGenerations();
    this.#tagsSaveQueue = Promise.resolve();
    this.#contextSaveQueue = Promise.resolve(undefined);
    this.#fieldLayoutSaveQueue = Promise.resolve();
    this.#pedagogicalVisibilitySaveQueue = Promise.resolve();
    this.#customFieldConfigSaveQueue = Promise.resolve();
    this.#settledPedagogicalFields = null;
    this.#settledCustomPedagogicalFields = null;
    this.#pedagogicalVisibilitySaveError.set(null);
    this.#pedagogicalValueGeneration.clear();
    this.#pedagogicalValueSavesInFlight = 0;
    this.#pedagogicalValueSaveStatus.set('idle');
    this.#contextSaveGeneration = 0;
    this.#saveUnavailable.set(false);
    this.#successfulPersistCount.set(0);
    this.#sectionImageSourceRejected.set(0);
    this.#sectionImageSourceRejectedHeld.set(false);
    this.#failedPedagogicalValueKeys.set(new Set());
  }

  /** Notifies consumers that a persist completed successfully (KT-200.5, contract C2). */
  #notifySuccessfulPersist(): void {
    this.#successfulPersistCount.update((count) => count + 1);
  }

  /**
   * Shared machinery for the immediate Kontextus link/unlink mutations (contract C10, S1). Applies
   * the optimistic context list at once, then serializes the server call on {@link #contextSaveQueue}
   * (only one context request in flight) and reconciles / rolls back under the generation guard.
   *
   * @param optimistic Produces the new context list to show immediately (add rows / remove a row).
   * @param dispatch Issues the server mutation for the instance id and returns the updated detail.
   * @returns A promise that settles when the dispatch and its reconciliation finish. No-op (resolved)
   *   when no draft is loaded.
   */
  #enqueueContextMutation(
    optimistic: (draft: ActivityEditDraft) => ActivityContextDocument[],
    dispatch: (id: string) => Promise<ActivityInstanceDetail>,
  ): Promise<void> {
    const previous = this.#draft();
    if (!previous) {
      return Promise.resolve();
    }

    // Claim the generation at ENQUEUE (call order), so an op enqueued before an earlier op's response
    // returns marks that earlier response stale (late-response guard, contract C10 / S1).
    const generation = ++this.#contextSaveGeneration;
    // Fallback rollback baseline for the FIRST op of an idle queue: the current (settled) list. It is
    // used ONLY when the queue carries no baseline yet (`undefined`, after load / reset). For any
    // QUEUED op the settled baseline is threaded through the queue from the previous op's outcome —
    // NOT read from the draft, which by dispatch time also holds this op's (and its predecessors')
    // not-yet-persisted optimistic rows. Capturing the draft here would restore a phantom row when
    // two ops fail in a row (C1-M2).
    const idleBaseline = previous.contextDocuments;
    // Optimistic rows apply IMMEDIATELY (targeted slice update, mirroring reconcile / rollback) so the
    // UI reflects the change synchronously, even while a dispatch waits behind the serialization queue.
    this.#draft.update((cur) => (cur ? { ...cur, contextDocuments: optimistic(cur) } : cur));

    // Serialize onto the context queue AND thread the server-settled baseline through it: the next
    // context request starts only after this one's response is processed, and each op rolls back (on
    // failure) to the list the SERVER last confirmed — the previous op's reconciled list, or the idle
    // baseline for the first op. `#dispatchContextMutation` handles its own errors and never rejects,
    // so the queue tail cannot stall on a failed mutation.
    const run = this.#contextSaveQueue.then((baseline) =>
      this.#dispatchContextMutation(previous.id, dispatch, baseline ?? idleBaseline, generation),
    );
    this.#contextSaveQueue = run;
    return this.#trackPending(this.#asNotCounted(run));
  }

  /**
   * Loads pedagogical fields for a resolved instance into the draft when the load epoch is still current.
   *
   * @param id - Activity instance id.
   * @param epoch - Resolution epoch; stale responses are ignored.
   * @returns Promise that settles after the draft slice is updated or cleared on error.
   */
  async #loadPedagogicalFieldsIntoDraft(id: string, epoch: number): Promise<void> {
    await this.#loadPedagogicalAndCustomFieldsIntoDraft(id, epoch);
  }

  async #loadPedagogicalAndCustomFieldsIntoDraft(id: string, epoch: number | null): Promise<void> {
    const stillCurrent = (): boolean => epoch === null || this.#isCurrentResolution(epoch);

    try {
      const [pedagogicalList, customList] = await Promise.all([
        this.#activityService.getPedagogicalFields(id),
        this.#activityService.getPedagogicalCustomFields(id),
      ]);
      if (!stillCurrent()) {
        return;
      }

      const pedagogicalItems = pedagogicalList.items.map((item) => ({ ...item }));
      const customItems = orderedCustomPedagogicalFields(customList.items).map((item) => ({ ...item }));
      this.#settledPedagogicalFields = pedagogicalItems.map((item) => ({ ...item }));
      this.#settledCustomPedagogicalFields = customItems.map((item) => ({ ...item }));
      this.#draft.update((cur) =>
        cur ? { ...cur, pedagogicalFields: pedagogicalItems, customPedagogicalFields: customItems } : cur,
      );
    } catch {
      if (!stillCurrent()) {
        return;
      }
      this.#draft.update((cur) => (cur ? { ...cur, pedagogicalFields: [], customPedagogicalFields: [] } : cur));
      this.#settledPedagogicalFields = [];
      this.#settledCustomPedagogicalFields = [];
    }
  }

  async #persistUpdateCustomPedagogicalField(
    id: string,
    fieldId: string,
    body: UpdateActivityPedagogicalCustomFieldRequest,
    baseline: ActivityPedagogicalCustomFieldResponse[],
  ): Promise<void> {
    const bindings = this.#customPedagogicalPersistBindings();
    await persistUpdateCustomPedagogicalField(
      this.#activityService,
      id,
      fieldId,
      body,
      baseline,
      bindings.readCustomFields,
      bindings.applyCustomFields,
      bindings.setSettled,
      bindings.draftId,
      bindings.onPersisted,
    );
  }

  async #persistCreateCustomPedagogicalField(
    id: string,
    request: CreateActivityPedagogicalCustomFieldRequest,
  ): Promise<void> {
    const bindings = this.#customPedagogicalPersistBindings();
    await persistCreateCustomPedagogicalField(
      this.#activityService,
      id,
      request,
      bindings.readCustomFields,
      bindings.applyCustomFields,
      bindings.setSettled,
      bindings.draftId,
      bindings.onPersisted,
    );
  }

  async #persistDeleteCustomPedagogicalField(
    id: string,
    fieldId: string,
    baseline: ActivityPedagogicalCustomFieldResponse[],
  ): Promise<void> {
    const bindings = this.#customPedagogicalPersistBindings();
    await persistDeleteCustomPedagogicalField(
      this.#activityService,
      id,
      fieldId,
      baseline,
      bindings.readCustomFields,
      bindings.applyCustomFields,
      bindings.setSettled,
      bindings.draftId,
      bindings.onPersisted,
    );
  }

  async #persistCustomPedagogicalVisibility(
    id: string,
    fieldId: string,
    visible: boolean,
    baseline: ActivityPedagogicalCustomFieldResponse[],
  ): Promise<void> {
    const bindings = this.#customPedagogicalPersistBindings();
    await persistCustomPedagogicalVisibility(
      this.#activityService,
      id,
      fieldId,
      visible,
      baseline,
      bindings.readCustomFields,
      bindings.applyCustomFields,
      bindings.setSettled,
      bindings.onPersisted,
    );
  }

  async #persistCustomPedagogicalReorder(
    id: string,
    fieldId: string,
    position: number,
    baseline: ActivityPedagogicalCustomFieldResponse[],
  ): Promise<void> {
    const bindings = this.#customPedagogicalPersistBindings();
    await persistCustomPedagogicalReorder(
      this.#activityService,
      id,
      fieldId,
      position,
      baseline,
      bindings.readCustomFields,
      bindings.applyCustomFields,
      bindings.setSettled,
      bindings.draftId,
      bindings.onPersisted,
    );
  }

  #customPedagogicalPersistBindings(): CustomPedagogicalPersistBindings {
    return {
      readCustomFields: (): ActivityPedagogicalCustomFieldResponse[] => this.#draft()?.customPedagogicalFields ?? [],
      applyCustomFields: (items: ActivityPedagogicalCustomFieldResponse[]): void => {
        this.#draft.update((cur) => (cur ? { ...cur, customPedagogicalFields: items } : cur));
      },
      setSettled: (items: ActivityPedagogicalCustomFieldResponse[]): void => {
        this.#settledCustomPedagogicalFields = cloneCustomPedagogicalFieldRows(items);
      },
      draftId: (): string | undefined => this.#draft()?.id,
      onPersisted: (): void => {
        this.#notifySuccessfulPersist();
      },
    };
  }

  async #persistPedagogicalVisibility(
    id: string,
    fieldName: PedagogicalFieldName,
    visible: boolean,
    baseline: ActivityPedagogicalFieldResponse[],
  ): Promise<void> {
    try {
      const list = await this.#activityService.updatePedagogicalFieldVisibility(id, fieldName, visible);
      const merged = mergePedagogicalFieldListFromServer(this.#draft()?.pedagogicalFields ?? [], list.items);
      this.#settledPedagogicalFields = merged.map((item) => ({ ...item }));
      this.#draft.update((cur) => (cur ? { ...cur, pedagogicalFields: merged } : cur));
      this.#notifySuccessfulPersist();
    } catch (error) {
      this.#pedagogicalVisibilitySaveError.set(error);
      this.#draft.update((cur) => (cur ? { ...cur, pedagogicalFields: baseline.map((item) => ({ ...item })) } : cur));
    }
  }

  async #persistPedagogicalReorder(
    id: string,
    fieldName: string,
    position: number,
    baseline: ActivityPedagogicalFieldResponse[],
  ): Promise<void> {
    try {
      const list = await this.#activityService.reorderPedagogicalField(id, fieldName, position);
      this.#applyPedagogicalList(id, list.items);
      this.#notifySuccessfulPersist();
    } catch (error) {
      if (this.#draft()?.id !== id) {
        return;
      }

      if (
        httpErrorStatus(error) === StatusCodes.CONFLICT &&
        httpErrorTitle(error) === PEDAGOGICAL_FIELD_REORDER_STALE
      ) {
        try {
          const list = await this.#activityService.getPedagogicalFields(id);
          this.#applyPedagogicalList(id, list.items);
          return;
        } catch (reloadError) {
          this.#pedagogicalVisibilitySaveError.set(reloadError);
          this.#restorePedagogicalSortOrder(baseline);
          return;
        }
      }

      this.#pedagogicalVisibilitySaveError.set(error);
      this.#restorePedagogicalSortOrder(baseline);
    }
  }

  #applyPedagogicalList(id: string, items: readonly ActivityPedagogicalFieldResponse[]): void {
    if (this.#draft()?.id !== id) {
      return;
    }

    const merged = mergePedagogicalFieldListFromServer(this.#draft()?.pedagogicalFields ?? [], items);
    this.#settledPedagogicalFields = merged.map((item) => ({ ...item }));
    this.#draft.update((cur) => (cur ? { ...cur, pedagogicalFields: merged } : cur));
  }

  #restorePedagogicalSortOrder(baseline: readonly ActivityPedagogicalFieldResponse[]): void {
    this.#draft.update((cur) =>
      cur ? { ...cur, pedagogicalFields: withPedagogicalSortOrderFromBaseline(cur.pedagogicalFields, baseline) } : cur,
    );
  }

  async #persistCustomPedagogicalValue(
    id: string,
    fieldId: string,
    value: string | null,
    generation: number,
  ): Promise<TrackedSaveOutcome> {
    let failed = false;
    let outcome: TrackedSaveOutcome = 'saved';

    try {
      const list = await this.#activityService.updatePedagogicalCustomFieldValue(id, fieldId, value);
      if (this.#pedagogicalValueGeneration.get(fieldId) !== generation) {
        outcome = 'superseded';
        return outcome;
      }

      const current = this.#draft()?.customPedagogicalFields ?? [];
      const serverRow = list.items.find((item) => item.id === fieldId);
      const merged = serverRow
        ? mergeCustomPedagogicalFieldRowFromServer(current, serverRow, value)
        : mergeCustomPedagogicalFieldListFromServer(current, list.items);
      this.#draft.update((cur) => (cur?.id === id ? { ...cur, customPedagogicalFields: merged } : cur));

      if (this.#settledCustomPedagogicalFields) {
        const settled = this.#settledCustomPedagogicalFields;
        const settledRow = list.items.find((item) => item.id === fieldId);
        this.#settledCustomPedagogicalFields = settledRow
          ? mergeCustomPedagogicalFieldRowFromServer(settled, settledRow, value)
          : mergeCustomPedagogicalFieldListFromServer(settled, list.items);
      }

      this.#notifySuccessfulPersist();
      return outcome;
    } catch {
      if (this.#pedagogicalValueGeneration.get(fieldId) === generation) {
        failed = true;
        outcome = 'failed';
      } else {
        outcome = 'superseded';
      }
      return outcome;
    } finally {
      const current = this.#pedagogicalValueGeneration.get(fieldId) === generation;
      this.#pedagogicalValueSavesInFlight = Math.max(0, this.#pedagogicalValueSavesInFlight - 1);

      if (current) {
        this.#markPedagogicalValueFailed(fieldId, failed);
      }

      if (current && failed) {
        this.#pedagogicalValueSaveStatus.set('error');
      } else if (current && this.#pedagogicalValueSavesInFlight === 0) {
        this.#pedagogicalValueSaveStatus.set('saved');
      } else if (
        !current &&
        this.#pedagogicalValueSavesInFlight === 0 &&
        this.#pedagogicalValueSaveStatus() === 'saving'
      ) {
        this.#pedagogicalValueSaveStatus.set('saved');
      }
    }
  }

  async #persistPedagogicalValue(
    id: string,
    fieldName: PedagogicalFieldName,
    value: string | null,
    generation: number,
  ): Promise<TrackedSaveOutcome> {
    let failed = false;
    let outcome: TrackedSaveOutcome = 'saved';

    try {
      const saved = await this.#activityService.updatePedagogicalFieldValue(id, fieldName, value);
      if (this.#pedagogicalValueGeneration.get(fieldName) !== generation) {
        outcome = 'superseded';
        return outcome;
      }

      this.#draft.update((cur) => {
        if (cur?.id !== id) {
          return cur;
        }

        return {
          ...cur,
          pedagogicalFields: cur.pedagogicalFields.map((item) => {
            if (item.fieldName !== fieldName || item.value !== value) {
              return item;
            }

            return { ...item, value: saved.value };
          }),
        };
      });

      if (this.#settledPedagogicalFields) {
        this.#settledPedagogicalFields = this.#settledPedagogicalFields.map((item) =>
          item.fieldName === fieldName ? { ...item, value: saved.value } : item,
        );
      }

      this.#notifySuccessfulPersist();
      return outcome;
    } catch {
      if (this.#pedagogicalValueGeneration.get(fieldName) === generation) {
        failed = true;
        outcome = 'failed';
      } else {
        outcome = 'superseded';
      }
      return outcome;
    } finally {
      const current = this.#pedagogicalValueGeneration.get(fieldName) === generation;
      this.#pedagogicalValueSavesInFlight = Math.max(0, this.#pedagogicalValueSavesInFlight - 1);

      if (current) {
        this.#markPedagogicalValueFailed(fieldName, failed);
      }

      if (current && failed) {
        this.#pedagogicalValueSaveStatus.set('error');
      } else if (current && this.#pedagogicalValueSavesInFlight === 0) {
        this.#pedagogicalValueSaveStatus.set('saved');
      } else if (
        !current &&
        this.#pedagogicalValueSavesInFlight === 0 &&
        this.#pedagogicalValueSaveStatus() === 'saving'
      ) {
        this.#pedagogicalValueSaveStatus.set('saved');
      }
    }
  }

  async #persistFieldLayout(): Promise<void> {
    const draft = this.#draft();
    if (!draft) {
      return;
    }

    const payload = draft.fieldLayout.map((item) => ({ ...item }));

    try {
      const updated = await this.#activityService.updateFieldLayout(draft.id, payload);
      const mapped = mapActivityInstanceDetailToEditDraft(updated);
      this.#draft.update((cur) => (cur ? { ...cur, fieldLayout: mapped.fieldLayout } : cur));
      this.#notifySuccessfulPersist();
    } catch {
      try {
        const detail = await this.#activityService.getActivityInstanceById(draft.id);
        const mapped = mapActivityInstanceDetailToEditDraft(detail);
        this.#draft.update((cur) => (cur ? { ...cur, fieldLayout: mapped.fieldLayout } : cur));
      } catch {
        // Keep the optimistic layout if reload also fails; the teacher can retry by toggling again.
      }
    }
  }

  /**
   * Dispatches a single serialized Kontextus mutation and reconciles the result onto the draft. On
   * success it replaces the draft's context list with the server-returned list (source of truth) and
   * returns THAT as the new settled baseline; on error it rolls the list back to the server-settled
   * baseline it was handed and returns it unchanged. Both branches early-return the unchanged baseline
   * when a newer context mutation has since started (generation guard), so a late / out-of-order
   * response never overwrites newer context state. Only the `contextDocuments` slice is touched, so a
   * concurrent field autosave is never clobbered. Handles its own errors — it never rejects. The
   * returned list is threaded to the next queued op as its rollback baseline.
   *
   * @param id The activity instance id the mutation targets.
   * @param dispatch Performs the mutation request and resolves with the refreshed activity detail.
   * @param baseline The server-settled context list to roll back to on failure.
   * @param generation The generation stamped for this mutation at enqueue time.
   * @returns A promise resolving to the settled context list to carry forward to the next queued op.
   */
  async #dispatchContextMutation(
    id: string,
    dispatch: (id: string) => Promise<ActivityInstanceDetail>,
    baseline: ActivityContextDocument[],
    generation: number,
  ): Promise<ActivityContextDocument[]> {
    try {
      const updated = await dispatch(id);
      if (!this.#isCurrentContextGeneration(generation)) {
        // Stale response: a newer op owns the draft display, so do NOT write it. But the server just
        // confirmed this list, so carry the FRESH server list forward as the settled baseline — it is
        // strictly fresher than the input baseline and is the correct rollback target for the
        // immediately-following op (nothing runs between them on the serialized queue). Returning the
        // stale input baseline here would let a failing successor roll back past a persisted change.
        return (updated.contextDocuments ?? []).map((doc) => ({ ...doc }));
      }
      // Reconcile from the returned detail: the server owns the context list. Deep-copy each row so
      // the draft never aliases the response snapshot; `?? []` guards a malformed response so it can
      // never throw inside the try and get misclassified as a failure.
      const reconciled = (updated.contextDocuments ?? []).map((doc) => ({ ...doc }));
      this.#draft.update((cur) => (cur ? { ...cur, contextDocuments: reconciled } : cur));
      this.#notifySuccessfulPersist();
      // The server just confirmed this list — it becomes the settled baseline for the next op.
      return reconciled;
    } catch {
      if (!this.#isCurrentContextGeneration(generation)) {
        // Stale rejection: a newer op owns the state — do not roll back, carry the baseline forward.
        return baseline;
      }
      // 400 / network error: roll the optimistic list back to the server-settled baseline.
      this.#draft.update((cur) => (cur ? { ...cur, contextDocuments: baseline } : cur));
      return baseline;
    }
  }

  /**
   * Builds the optimistic context list for a link: appends the new `{ id, fileName }` refs that are
   * not already present (idempotent, mirroring the backend link rule), preserving existing rows. The
   * incoming batch is itself deduped by id, so `[A, A]` yields a SINGLE optimistic row.
   *
   * @param draft The current draft.
   * @param refs The full refs being linked.
   * @returns The optimistic context list.
   */
  #withOptimisticLinkedRows(draft: ActivityEditDraft, refs: ActivityContextDocument[]): ActivityContextDocument[] {
    const seenIds = new Set(draft.contextDocuments.map((doc) => doc.id));
    const added: ActivityContextDocument[] = [];
    for (const ref of refs) {
      if (seenIds.has(ref.id)) {
        continue;
      }
      seenIds.add(ref.id);
      added.push({
        ...ref,
        linkType: ref.linkType ?? ActivityDocumentLinkType.OTHER,
      });
    }
    return [...draft.contextDocuments, ...added];
  }

  /**
   * True when `generation` is still the current Kontextus mutation generation — i.e. no newer
   * link/unlink has started since this one claimed it. A stale response uses this to bail out.
   *
   * @param generation The generation claimed by a mutation at dispatch time.
   * @returns Whether that mutation is still the current one.
   */
  #isCurrentContextGeneration(generation: number): boolean {
    return generation === this.#contextSaveGeneration;
  }

  /**
   * Phase 1 of {@link resolveDeepLink} (S7/S6): load the teacher's OWN instance into the draft.
   *
   * @param id The activity id being resolved.
   * @param epoch The resolution's epoch, re-checked after the GET await for race safety.
   * @returns `loaded` when the instance is ours, `peerInstance` when the row is readable but not
   *   owned, `error` on a non-404 transient failure, `superseded` when a newer resolution overtook
   *   this one, or `null` on a 404 ("not mine") to signal the caller to fall through to the template
   *   probe.
   */
  async #resolveOwnInstance(id: string, epoch: number): Promise<ActivityEditDeepLinkOutcome | null> {
    try {
      const ownInstance = await this.#activityService.getActivityInstanceById(id);
      // A newer resolution overtook us across the GET await: write nothing, the latest wins.
      if (!this.#isCurrentResolution(epoch)) {
        return { outcome: 'superseded' };
      }
      if (!ownInstance.isOwner) {
        this.#isLoading.set(false);
        return { outcome: 'peerInstance', activityId: id };
      }
      this.#draft.set(mapActivityInstanceDetailToEditDraft(ownInstance));
      this.#draftLoadCount.update((count) => count + 1);
      this.#coverQueue.remember(ownInstance.id, ownInstance);
      this.#coverQueue.enqueue(loadedCoverCandidate(ownInstance));
      await this.#loadPedagogicalFieldsIntoDraft(id, epoch);
      if (!this.#isCurrentResolution(epoch)) {
        return { outcome: 'superseded' };
      }
      this.#isLoading.set(false);
      return { outcome: 'loaded' };
    } catch (error) {
      if (!this.#isCurrentResolution(epoch)) {
        return { outcome: 'superseded' };
      }
      // Only a 404 means "not my instance — maybe a library template". Any other error (5xx /
      // network) is a TRANSIENT failure on the teacher's OWN instance: we must NOT fork on it
      // (fork is issued only for a confirmed template) AND we must NOT eject the teacher to the
      // library — a valid activity of theirs failed to load transiently, so they stay on the
      // page with the error state (S18(a)). A 404 falls through (null) to the template probe.
      if (this.#isNotFound(error)) {
        return null;
      }
      this.#loadError.set(error);
      this.#isLoading.set(false);
      return { outcome: 'error' };
    }
  }

  /**
   * Phase 2 of {@link resolveDeepLink} (S5/S25): probe whether the id is a library template.
   *
   * @param id The activity id being resolved.
   * @param epoch The resolution's epoch, re-checked after the GET await for race safety.
   * @returns `unresolvable` on a true 404 (neither instance nor template), `error` on a non-404
   *   transient probe failure, `superseded` when a newer resolution overtook this one, or `null`
   *   when the template resolved — signaling the caller to fork it.
   */
  async #probeTemplate(id: string, epoch: number): Promise<ActivityEditDeepLinkOutcome | null> {
    try {
      await this.#activityService.getActivityById(id, 'template');
      // Re-check BEFORE the fork: a newer resolution here means we must not issue the `:copy` this
      // stale resolution would otherwise fire — that is the unwanted second fork.
      return this.#isCurrentResolution(epoch) ? null : { outcome: 'superseded' };
    } catch (error) {
      if (!this.#isCurrentResolution(epoch)) {
        return { outcome: 'superseded' };
      }
      this.#loadError.set(error);
      this.#isLoading.set(false);

      // A TRUE 404 means neither an instance nor a template resolves (deleted / never-existed /
      // another teacher's private instance). NO `:copy` is issued and the page redirects to the
      // library (S25). A non-404 (5xx / network) is a TRANSIENT template-probe failure, not a
      // not-found: keep the teacher on the page with the error state (S18(a)), do NOT redirect.
      return this.#isNotFound(error) ? { outcome: 'unresolvable' } : { outcome: 'error' };
    }
  }

  /**
   * Phase 3 of {@link resolveDeepLink} (S5/S6, C5): fork the resolved library template into a
   * private instance. The empty-body `{resource}:copy` creates the copy from the library source;
   * the original library row is never written. The page then swaps the URL to the new instance
   * (replaceUrl), which re-activates the route and resolves as `loaded` — so a refresh cannot
   * re-fork (S6). Loading stays true through the swap; the re-activation resets it.
   *
   * Epoch re-check placement (accepted best-effort limit): the epoch is only re-checked AFTER
   * `copyActivity` resolves — it cannot be checked mid-flight. So if a newer resolution supersedes
   * this one WHILE the copy POST is running, the copy has ALREADY been created server-side by the
   * time the re-check runs; this returns `superseded` (the page does not swap the URL to it), but
   * the just-created private copy may linger as an orphan in the teacher's library. A POST-based
   * fork is not client-recallable, so this is accepted, consistent with the plan's Q6
   * client-side-dedup tech-debt; a compensating server-side delete is a separate product decision
   * (out of scope). The `:copy` is only guaranteed skipped when the supersession lands BEFORE this
   * phase is reached (during the instance- or template-GET) — see {@link resolveDeepLink}.
   *
   * @param id The library template id being forked.
   * @param epoch The resolution's epoch, re-checked after the `:copy` await for race safety.
   * @returns `forked` with the new instance id, `error` on a transient copy failure, or
   *   `superseded` when a newer resolution overtook this one during the `:copy` — the copy WAS
   *   created (and may remain as an orphan, see above), but the URL must NOT swap to it: the latest
   *   resolution owns the URL.
   */
  async #forkTemplate(id: string, epoch: number): Promise<ActivityEditDeepLinkOutcome> {
    try {
      const newInstanceId = await this.#activityService.copyActivity(id, 'template');
      this.#coverQueue.enqueue(createdCoverCandidate(newInstanceId));
      return this.#isCurrentResolution(epoch) ? { outcome: 'forked', newInstanceId } : { outcome: 'superseded' };
    } catch (error) {
      if (!this.#isCurrentResolution(epoch)) {
        return { outcome: 'superseded' };
      }
      // A failed fork is a TRANSIENT error, NOT a not-found: the template DID resolve, only the
      // copy failed. Keep the teacher on the page with the error state so they can retry — do
      // NOT redirect to the library (the `unresolvable`/library redirect is reserved strictly for
      // the true 404 not-found case, S25).
      this.#loadError.set(error);
      this.#isLoading.set(false);
      return { outcome: 'error' };
    }
  }

  /**
   * True when `epoch` is still the current deep-link resolution epoch — i.e. no newer
   * {@link resolveDeepLink} has started since this one claimed `epoch`. A stale (superseded)
   * resolution uses this to bail out before writing any signal or issuing a side effect.
   *
   * @param epoch The epoch claimed by a resolution at dispatch time.
   * @returns Whether that resolution is still the current one.
   */
  #isCurrentResolution(epoch: number): boolean {
    return epoch === this.#resolveEpoch;
  }

  /**
   * True when `error` is (or carries) an HTTP 404 — the "not found / not mine" signal.
   *
   * @param error The rejection to inspect.
   * @returns Whether the error is an HTTP 404.
   */
  #isNotFound(error: unknown): boolean {
    return httpErrorStatus(error) === StatusCodes.NOT_FOUND;
  }

  /**
   * True when a sections save was rejected because a section references a non-KT image source.
   *
   * @param error The rejection to inspect.
   * @returns Whether the ProblemDetails title is the image-source-not-allowed marker.
   */
  #isSectionImageSourceRejected(error: unknown): boolean {
    return httpErrorTitle(error) === ACTIVITY_CONTENT_SECTION_IMAGE_SOURCE_NOT_ALLOWED;
  }

  /**
   * Dispatches a single field commit and reconciles the result onto the draft: on success it
   * merges ONLY the committed field's slice from the full response (so a concurrent save of a
   * sibling field is not clobbered — KT-155); on error it reverts ONLY that field's slice to
   * its pre-commit value (S18a). A response is discarded if a newer generation exists for the field
   * (S28, C1). A 404 additionally raises {@link saveUnavailable} so the page ejects to the library
   * (S18b). Handles its own errors — it never rejects.
   *
   * @param previous The draft snapshot captured before this commit's optimistic update.
   * @param commit The field commit being persisted.
   * @param generation The generation stamped for this commit at dispatch time.
   * @returns A promise that resolves, once the response has been reconciled onto the draft, to how the save ended.
   */
  async #dispatchAndReconcile(
    previous: ActivityEditDraft,
    commit: ActivityEditFieldCommit,
    generation: number,
  ): Promise<TrackedSaveOutcome> {
    const { field } = commit;

    try {
      const updated = await this.#dispatchCommit(previous.id, commit);

      if (!this.#isCurrentGeneration(field, generation)) {
        return 'superseded';
      }

      // Merge ONLY the committed field's slice from the response onto the CURRENT
      // draft, so a concurrent save of a sibling field is not clobbered (KT-155).
      const mappedUpdated = mapActivityInstanceDetailToEditDraft(updated);
      this.#draft.update((cur) => (cur ? this.#mergeFieldSlice(cur, mappedUpdated, field) : mappedUpdated));
      this.#setFieldSaveStatus(field, 'saved');
      if (field === 'sections') {
        this.#sectionImageSourceRejectedHeld.set(false);
      }
      this.#notifySuccessfulPersist();
      return 'saved';
    } catch (error) {
      if (!this.#isCurrentGeneration(field, generation)) {
        return 'superseded';
      }

      if (field === 'sections' && this.#isSectionImageSourceRejected(error)) {
        this.#setFieldSaveStatus(field, 'error');
        this.#sectionImageSourceRejected.update((count) => count + 1);
        this.#sectionImageSourceRejectedHeld.set(true);
        return 'imageSourceRejected';
      }

      // Revert ONLY the committed field's slice back to its pre-commit value,
      // leaving concurrent sibling edits intact (KT-155 post-error revert, C1). This alone is the
      // S18a behavior for a generic error (5xx / network / uncaught 400): the teacher stays on the
      // page with a visible error and may retry.
      this.#draft.update((cur) => (cur ? this.#mergeFieldSlice(cur, previous, field) : previous));
      this.#setFieldSaveStatus(field, 'error');
      if (field === 'sections') {
        this.#sectionImageSourceRejectedHeld.set(false);
      }

      // S18b: a 404 means the row became unresolvable mid-edit (deleted / ownership lost). Reverting
      // alone would mislead — the teacher would keep "editing" a row that no longer exists — so raise
      // the distinct save-unavailable signal the page reacts to (disable editing + redirect to the
      // library with a distinct message, the same end-state as an unresolvable deep-link, S25).
      if (this.#isNotFound(error)) {
        this.#saveUnavailable.set(true);
      }
      return 'failed';
    }
  }

  /**
   * True for the chip dimensions that all commit through the shared `PUT …/tags` endpoint
   * and therefore must be serialized (Q21). The value-carrying fields (name, description,
   * instructions, duration) and the sub-topic fields each own a distinct endpoint.
   *
   * @param field The committed field.
   * @returns Whether the field commits through the shared `tags` endpoint.
   */
  #isTagsField(field: ActivityDetailEditableField): boolean {
    switch (field) {
      case 'grade':
      case 'subject':
      case 'subtype':
      case 'locations':
      case 'tools':
      case 'type':
      case 'workform': {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  #dispatchCommit(id: string, commit: ActivityEditFieldCommit): Promise<ActivityInstanceDetail> {
    switch (commit.field) {
      case 'name': {
        return this.#activityService.updateName(id, commit.value);
      }
      case 'description': {
        return this.#activityService.updateDescription(id, commit.value);
      }
      case 'instructions': {
        return this.#activityService.updateInstructions(id, commit.value);
      }
      case 'sections': {
        // Sections commit through their OWN `PUT …/sections` endpoint (full-list replace, C1/C3),
        // NOT the shared `tags` endpoint — so this dispatch runs on the direct per-field
        // generation-guard path and is never serialized on `#tagsSaveQueue`.
        return this.#activityService.updateSections(id, commit.value);
      }
      case 'duration': {
        return this.#activityService.updateDuration(id, commit.value);
      }
      case 'topic':
      case 'subtopic': {
        return this.#activityService.updateSubTopic(id, commit.value.subTopicId);
      }
      case 'visibility': {
        return this.#activityService.updateVisibility(id, commit.value);
      }
      case 'subject':
      case 'grade':
      case 'type':
      case 'subtype':
      case 'locations':
      case 'tools':
      case 'workform': {
        // The edit page resolves the single-dimension replacement via
        // `activity-tag-replacement.util.ts` (preserving EVERY other dimension, including
        // the ones this slice does not model) and passes the full, resolved tag-id set in
        // `commit.value.tagIds` — the store never hand-rolls the concat (C4/S26). These
        // commits are serialized upstream in `commitField` so only one `PUT …/tags` is ever
        // in flight (Q21); here the resolved set is simply forwarded to the endpoint.
        return this.#activityService.updateTags(id, commit.value.tagIds);
      }
    }
  }

  #applyOptimisticUpdate(draft: ActivityEditDraft, commit: ActivityEditFieldCommit): ActivityEditDraft {
    switch (commit.field) {
      case 'name': {
        return { ...draft, name: commit.value };
      }
      case 'description': {
        return { ...draft, description: commit.value };
      }
      case 'instructions': {
        return { ...draft, instructions: [...commit.value] };
      }
      case 'sections': {
        // Deep-copy each section object so the optimistic list never aliases the caller's items.
        return { ...draft, sections: commit.value.map((section) => ({ ...section })) };
      }
      case 'duration': {
        return { ...draft, duration: { ...commit.value } };
      }
      case 'topic':
      case 'subtopic': {
        return {
          ...draft,
          subTopicId: commit.value.subTopicId,
          subTopicName: commit.value.subTopicName ?? draft.subTopicName,
          topicName: commit.value.topicName ?? draft.topicName,
        };
      }
      case 'visibility': {
        return { ...draft, visibility: commit.value };
      }
      case 'subject':
      case 'grade':
      case 'type':
      case 'subtype':
      case 'locations':
      case 'tools':
      case 'workform': {
        return { ...draft, tags: [...commit.value.tags] };
      }
    }
  }

  /**
   * Copies only the properties owned by `field` from `source` onto `current`,
   * so a concurrent save of another field does not clobber sibling optimistic
   * state (ported from the KT-155 dialog `#mergeFieldSlice`).
   *
   * @param current The current draft to merge onto.
   * @param source The draft to copy the field's slice from.
   * @param field The committed field whose slice is merged.
   * @returns The draft with only the field's slice replaced from `source`.
   */
  #mergeFieldSlice(
    current: ActivityEditDraft,
    source: ActivityEditDraft,
    field: ActivityDetailEditableField,
  ): ActivityEditDraft {
    switch (field) {
      case 'name': {
        return { ...current, name: source.name };
      }
      case 'description': {
        return { ...current, description: source.description };
      }
      case 'instructions': {
        return { ...current, instructions: source.instructions };
      }
      case 'sections': {
        return { ...current, sections: source.sections };
      }
      case 'duration': {
        return { ...current, duration: source.duration };
      }
      case 'topic':
      case 'subtopic': {
        return {
          ...current,
          topicName: source.topicName,
          subTopicName: source.subTopicName,
          subTopicId: source.subTopicId,
        };
      }
      case 'visibility': {
        return { ...current, visibility: source.visibility };
      }
      case 'subject':
      case 'grade':
      case 'type':
      case 'subtype':
      case 'locations':
      case 'tools':
      case 'workform': {
        return { ...current, tags: source.tags };
      }
    }
  }

  #setFieldSaveStatus(field: ActivityDetailEditableField, status: ActivityEditFieldSaveStatus): void {
    this.#fieldSaveStatuses.update((statuses) => ({ ...statuses, [field]: status }));
  }

  #nextGeneration(field: ActivityDetailEditableField): number {
    return ++this.#saveGeneration[field];
  }

  #isCurrentGeneration(field: ActivityDetailEditableField, generation: number): boolean {
    return generation === this.#saveGeneration[field];
  }
}
